/**
 * @author michalstolarski
 * @since 2022-3-28
 */

import React from "react";
import styled, { css, useTheme } from "styled-components";

import { TestWrapper } from "@ui/Atoms";
import { rem } from "@ui/helpers";
import { Icon } from "@ui/Assets/Symbolicons/Symbolicons";

import { Color, defaultColor } from "./MobileActionMenu.helpers";

interface Props {
  className?: string;
  children: React.ReactNode;
  icon?: React.ReactElement;
  iconColor?: Color;
  isIconEnabled?: boolean;
  isIconOldVersion?: boolean;
  isActive?: boolean;
  isDisabled?: boolean;
  label?: React.ReactNode;
  onClick: () => void;
  testId?: string;
}

const MobileActionMenuItem = (props: Props): React.ReactElement => {
  const theme = useTheme();

  return (
    <li className={props.className}>
      <TestWrapper testId={props.testId}>
        <Button
          isDisabled={props.isDisabled}
          isActive={props.isActive}
          onClick={props.onClick}
        >
          {props.isIconEnabled && (
            <IconWrapper isOldVersion={props.isIconOldVersion}>
              {!!props.icon &&
                React.isValidElement<Icon>(props.icon) &&
                React.cloneElement(props.icon, {
                  size: props.isIconOldVersion
                    ? theme.icons.sizes.base_x2
                    : theme.icons.sizes.base_x3,
                  color: props.isDisabled
                    ? theme.palette.grey400
                    : theme.palette[props.iconColor || defaultColor],
                })}
            </IconWrapper>
          )}
          <Content>{props.children}</Content>
          {props.label && <Label>{props.label}</Label>}
        </Button>
      </TestWrapper>
    </li>
  );
};

export { Props as MobileActionMenuItemProps, MobileActionMenuItem };

const Button = styled.button<{ isActive?: boolean; isDisabled?: boolean }>`
  min-height: ${rem(48)};
  width: 100%;
  padding: ${({ theme }) =>
    `${theme.margins.base_x1_5} ${theme.margins.base_x3}`};
  transition: background-color ${props => props.theme.animations.duration};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.margins.base_x2};
  outline: none;
  color: ${props => props.theme.palette.grey700};

  ${props =>
    props.isDisabled &&
    css`
      color: ${props.theme.palette.grey400};
      pointer-events: none;
    `}

  ${props =>
    props.isActive &&
    !props.isDisabled &&
    css`
      font-weight: ${props.theme.fonts.weights.bold};
    `}

      &:hover {
    ${props =>
      !props.isDisabled &&
      css`
        background-color: ${props.theme.palette.grey50};
      `}
  }
`;

const IconWrapper = styled.span<{ isOldVersion?: boolean }>`
  width: ${props => rem(props.theme.icons.sizes.base_x3)};
  height: ${props => rem(props.theme.icons.sizes.base_x3)};
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Content = styled.span`
  text-align: left;
`;

const Label = styled.span`
  align-self: flex-start;
`;
